import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FolderOpen,
  PackageOpen,
  FilePlus2,
  Search,
  ChevronLeft,
  ChevronRight,
  AlertCircle,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Loader2,
} from 'lucide-react';
import {
  listCases,
  type Case,
  type CaseListParams,
} from '../api/cases';
import {
  CASE_LABELS,
  CASE_STATUS_LABELS,
  TIER_LABELS,
  COMMON_LABELS,
} from '../config/labels';

type SortField = 'case_number' | 'issuer_name' | 'ticker' | 'depositor_name' | 'status' | 'tier' | 'created_at';
type SortDir = 'asc' | 'desc';

// 'My cases only' filter is default-ON and remembered for the browser session
// (survives reloads + navigation, clears when the tab closes).
const MINE_ONLY_KEY = 'dr_cases_mine_only';
function readMineOnly(): boolean {
  try {
    const raw = sessionStorage.getItem(MINE_ONLY_KEY);
    if (raw === 'true') return true;
    if (raw === 'false') return false;
  } catch {
    // sessionStorage unavailable (private mode / disabled) -> fall back to default
  }
  return true; // default: checked
}

const STATUS_OPTIONS = Object.keys(CASE_STATUS_LABELS);
const TIER_OPTIONS = Object.keys(TIER_LABELS);

function tierBadgeClass(tier?: string): string {
  if (tier?.toUpperCase() === 'STANDARD') return 'badge-standard';
  if (tier?.toUpperCase() === 'ENHANCED_REVIEW') return 'badge-enhanced';
  if (tier?.toUpperCase() === 'ESCALATE') return 'badge-escalate';
  return 'badge bg-slate-100 text-slate-600';
}

function statusPillClass(status: string): string {
  const map: Record<string, string> = {
    DRAFT: 'bg-slate-100 text-slate-700',
    INTAKE: 'bg-blue-100 text-blue-700',
    PUBLIC_LOOKUP: 'bg-indigo-100 text-indigo-700',
    DOC_ANALYSIS: 'bg-purple-100 text-purple-700',
    SCORING: 'bg-amber-100 text-amber-700',
    SUMMARY: 'bg-cyan-100 text-cyan-700',
    PENDING_DETERMINATION: 'bg-orange-100 text-orange-700',
    DETERMINED: 'bg-green-100 text-green-700',
    CLOSED: 'bg-slate-200 text-slate-600',
    ESCALATED: 'bg-red-100 text-red-700',
    SUBMITTED: 'bg-blue-100 text-blue-700',
    IN_REVIEW: 'bg-indigo-100 text-indigo-700',
    RETURNED: 'bg-amber-100 text-amber-700',
    APPROVED: 'bg-green-100 text-green-700',
  };
  return `badge ${map[status] || 'bg-slate-100 text-slate-600'}`;
}

function formatDate(iso?: string): string {
  if (!iso) return '-';
  try {
    return new Date(iso).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return '-';
  }
}

export default function CasesList() {
  const navigate = useNavigate();

  const [cases, setCases] = useState<Case[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [pageSize] = useState(20);

  // Filters
  const [statusFilter, setStatusFilter] = useState('');
  const [tierFilter, setTierFilter] = useState('');
  const [searchText, setSearchText] = useState('');
  const [mineOnly, setMineOnly] = useState<boolean>(readMineOnly);

  // Sorting
  const [sortField, setSortField] = useState<SortField>('created_at');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  const fetchCases = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params: CaseListParams = { page, page_size: pageSize };
      if (statusFilter) params.status = statusFilter;
      if (mineOnly) params.mine_only = true;
      const res = await listCases(params);
      setCases(res.items || []);
      setTotal(res.total || 0);
    } catch (err) {
      setError('Failed to load cases. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, statusFilter, mineOnly]);

  useEffect(() => {
    fetchCases();
  }, [fetchCases]);

  // Client-side filtering for tier and search
  const filteredCases = useMemo(() => {
    let result = [...cases];
    if (tierFilter) {
      result = result.filter((c) => c.tier === tierFilter);
    }
    if (searchText.trim()) {
      const q = searchText.toLowerCase();
      result = result.filter(
        (c) =>
          (c.issuer_name || '').toLowerCase().includes(q) ||
          (c.depositor_name || '').toLowerCase().includes(q) ||
          (c.ticker || '').toLowerCase().includes(q) ||
          (c.case_number || '').toLowerCase().includes(q)
      );
    }
    // Sort
    result.sort((a, b) => {
      const av = (a[sortField] || '') as string;
      const bv = (b[sortField] || '') as string;
      const cmp = av.localeCompare(bv);
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return result;
  }, [cases, tierFilter, searchText, sortField, sortDir]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const sortIcon = (field: SortField) => {
    if (sortField !== field) return <ArrowUpDown size={14} className="text-slate-400" />;
    return sortDir === 'asc' ? (
      <ArrowUp size={14} className="text-primary-600" />
    ) : (
      <ArrowDown size={14} className="text-primary-600" />
    );
  };

  const columns: { key: SortField; label: string; sortable: boolean }[] = [
    { key: 'case_number', label: CASE_LABELS.caseNumber, sortable: true },
    { key: 'issuer_name', label: CASE_LABELS.issuer, sortable: true },
    { key: 'ticker', label: CASE_LABELS.symbol, sortable: true },
    { key: 'depositor_name', label: CASE_LABELS.depositor, sortable: true },
    { key: 'status', label: CASE_LABELS.status, sortable: true },
    { key: 'tier', label: CASE_LABELS.tier, sortable: true },
    { key: 'created_at', label: CASE_LABELS.createdAt, sortable: true },
  ];

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <h1 className="text-2xl font-bold text-slate-900">Cases</h1>
        <div className="flex gap-3">
          <button
            onClick={() => navigate('/cases/package-drop')}
            className="btn-primary px-5 py-2.5 text-base"
            aria-label="Create case automatically from package drop"
          >
            <PackageOpen size={20} />
            {CASE_LABELS.createAutomatic}
          </button>
          <button
            onClick={() => navigate('/cases/new')}
            className="btn-secondary"
            aria-label="Create case manually"
          >
            <FilePlus2 size={18} />
            {CASE_LABELS.createManual}
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="card p-4 mb-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1 relative">
            <Search
              className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
              size={18}
            />
            <input
              type="text"
              className="input pl-10"
              placeholder={CASE_LABELS.searchPlaceholder}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              aria-label="Search cases"
            />
          </div>
          <select
            className="input sm:w-48"
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(1);
            }}
            aria-label="Filter by status"
          >
            <option value="">{CASE_LABELS.allStatuses}</option>
            {STATUS_OPTIONS.map((s) => (
              <option key={s} value={s}>
                {CASE_STATUS_LABELS[s]}
              </option>
            ))}
          </select>
          <select
            className="input sm:w-44"
            value={tierFilter}
            onChange={(e) => setTierFilter(e.target.value)}
            aria-label="Filter by tier"
          >
            <option value="">{CASE_LABELS.allTiers}</option>
            {TIER_OPTIONS.map((t) => (
              <option key={t} value={t}>
                {TIER_LABELS[t]}
              </option>
            ))}
          </select>
          <label className="flex items-center gap-2 text-sm text-slate-600 sm:w-40">
            <input
              type="checkbox"
              checked={mineOnly}
              onChange={(e) => {
                const v = e.target.checked;
                setMineOnly(v);
                setPage(1);
                try { sessionStorage.setItem(MINE_ONLY_KEY, String(v)); } catch { /* ignore */ }
              }}
              className="rounded border-slate-300"
            />
            My cases only
          </label>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="flex items-center gap-2 p-3 rounded-lg bg-red-50 text-red-700 text-sm border border-red-200 mb-4">
          <AlertCircle size={16} />
          <span>{error}</span>
          <button onClick={fetchCases} className="ml-auto text-red-600 hover:underline">
            {COMMON_LABELS.retry}
          </button>
        </div>
      )}

      {/* Table */}
      <div className="card overflow-hidden">
        {loading ? (
          <div className="p-6 space-y-3">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="flex gap-4">
                <div className="skeleton h-5 w-24" />
                <div className="skeleton h-5 w-32" />
                <div className="skeleton h-5 w-16" />
                <div className="skeleton h-5 w-28" />
                <div className="skeleton h-5 w-20" />
                <div className="skeleton h-5 w-16" />
                <div className="skeleton h-5 w-24" />
              </div>
            ))}
          </div>
        ) : filteredCases.length === 0 ? (
          <div className="p-12 flex flex-col items-center text-center">
            <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mb-4">
              <FolderOpen className="text-slate-400" size={32} />
            </div>
            <h2 className="text-lg font-semibold text-slate-700 mb-1">
              {CASE_LABELS.noCases}
            </h2>
            <p className="text-sm text-slate-500 mb-4 max-w-sm">
              {CASE_LABELS.noCasesDesc}
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => navigate('/cases/package-drop')}
                className="btn-primary"
              >
                <PackageOpen size={18} />
                {CASE_LABELS.dropPackage}
              </button>
              <button
                onClick={() => navigate('/cases/new')}
                className="btn-secondary"
              >
                <FilePlus2 size={18} />
                {CASE_LABELS.createFirst}
              </button>
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50">
                  {columns.map((col) => (
                    <th
                      key={col.key}
                      className="px-4 py-3 text-left font-medium text-slate-600"
                    >
                      {col.sortable ? (
                        <button
                          onClick={() => handleSort(col.key)}
                          className="flex items-center gap-1 hover:text-slate-900 transition-colors"
                        >
                          {col.label}
                          {sortIcon(col.key)}
                        </button>
                      ) : (
                        col.label
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredCases.map((c) => (
                  <tr
                    key={c.id}
                    onClick={() => navigate(`/cases/${c.id}`)}
                    className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer transition-colors"
                  >
                    <td className="px-4 py-3 font-medium text-slate-900">
                      {c.case_number || c.id.slice(0, 8)}
                    </td>
                    <td className="px-4 py-3 text-slate-700">
                      {c.issuer_name || '-'}
                    </td>
                    <td className="px-4 py-3 text-slate-700">
                      {c.ticker ? (
                        <span className="font-mono font-medium">{c.ticker}</span>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td className="px-4 py-3 text-slate-700">
                      {c.depositor_name || '-'}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span className={statusPillClass(c.status)}>
                          {c.current_stage || CASE_STATUS_LABELS[c.status] || c.status}
                        </span>
                        {c.review_in_progress && (
                          <span className="inline-flex items-center gap-1 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-full px-2 py-0.5">
                            <Loader2 size={11} className="animate-spin" />
                            Review in progress
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {c.tier ? (
                        <span className={tierBadgeClass(c.tier)}>
                          {TIER_LABELS[c.tier] || c.tier}
                        </span>
                      ) : (
                        <span className="text-slate-400">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-slate-500">
                      {formatDate(c.created_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {!loading && total > 0 && (
        <div className="flex items-center justify-between mt-4">
          <p className="text-sm text-slate-500">
            {CASE_LABELS.page} {page} {CASE_LABELS.of} {totalPages} ({total} total)
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="btn-secondary px-3 py-1.5"
              aria-label="Previous page"
            >
              <ChevronLeft size={16} />
              {CASE_LABELS.prev}
            </button>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              className="btn-secondary px-3 py-1.5"
              aria-label="Next page"
            >
              {CASE_LABELS.next}
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}